import { useState, useEffect, useRef } from "react";
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './LLMSidebar.css';
import { FaComments } from "react-icons/fa";
import { SiPostgresql } from "react-icons/si";
import { AiOutlineFileText } from "react-icons/ai";
import { MdDashboard } from "react-icons/md";
import { MdTableChart } from "react-icons/md";
import logo from './logo_white.svg'; 
import { secureStorage } from "./utils/secureStorage";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const LLMSidebar = ({
  setFeature,
  setActiveChat,
  activeChat,
  refreshTrigger,
  theme,
  toggleTheme,
}) => {
  const [recentChats, setRecentChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [role] = useState(secureStorage.getItem("role") || "staff");
  const [chatUsage, setChatUsage] = useState(null);
  const [chatTokenUsage, setChatTokenUsage] = useState(null);
  const [orgUsage, setOrgUsage] = useState(null);
  const [orgTokenUsage, setOrgTokenUsage] = useState(null);
  const isSuperAdmin = secureStorage.getItem("isSuperAdmin") === "true";

  const refreshToken = secureStorage.getItem("refreshToken");
  const dropdownRef = useRef(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const currentPlan = secureStorage.getItem("tier") || "Free";



  useEffect(() => {
    document.body.setAttribute("data-theme", theme);
    secureStorage.setItem("theme", theme);
  }, [theme]);

  const axiosConfig = () => ({
    headers: {
      Authorization: `Bearer ${secureStorage.getItem("token")}`,
    },
  });

  const refreshAccessToken = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/refresh`, {
        refresh_token: refreshToken,
      });
      const newToken = response.data.access_token;
      secureStorage.setItem("token", newToken);
      return newToken;
    } catch (err) {
      console.error("Token refresh failed:", err);
      handleLogout();
      throw err;
    }
  };

  const withAuthRetry = async (requestFn) => {
    try {
      return await requestFn();
    } catch (err) {
      if (err.response && err.response.status === 401) {
        await refreshAccessToken();
        return await requestFn();
      } else {
        throw err;
      }
    }
  };

  const handleFeatureChange = (featureName) => {
    setFeature(featureName);
    secureStorage.setItem("feature", featureName);
  };

  const fetchRecentChats = async () => {
    try {
      const response = await withAuthRetry(() =>
        axios.get(`${API_BASE_URL}/api/chat/sessions`, axiosConfig())
      );
      const sorted = response.data.sort((a, b) =>
        new Date(b.updated_at || b.created_at) -
        new Date(a.updated_at || a.created_at)
      );
      setRecentChats(sorted);
    } catch (error) {
      console.error("Error fetching recent chats:", error);
      setError("Failed to load recent chats. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageData = async () => {
    try {
      const chatRes = await withAuthRetry(() =>
        axios.get(`${API_BASE_URL}/api/chat/storage-usage`, axiosConfig())
      );
      setChatUsage(chatRes.data);

      if (role === "admin") {
        const orgRes = await withAuthRetry(() =>
          axios.get(`${API_BASE_URL}/admin/storage-usage`, axiosConfig())
        );
        setOrgUsage(orgRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch usage stats:", error);
    }
  };

  const fetchUsageToken = async () => {
    try {
      const res = await withAuthRetry(() =>
        axios.get(`${API_BASE_URL}/usage-summary`, axiosConfig())
      );

      setChatTokenUsage((prev) => ({
        ...prev,
        tokens_used: res.data.user.tokens_used,
        token_limit: res.data.user.token_limit,
      }));

      if (role === "admin") {
        setOrgTokenUsage((prev) => ({
          ...prev,
          tokens_used: res.data.org.tokens_used,
          token_cap: res.data.org.token_cap,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch usage summary:", error);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      fetchRecentChats();
      fetchUsageData();
      fetchUsageToken();
    }
  }, [refreshTrigger, activeChat, role, isSuperAdmin]);

  const handleLogout = () => {
    secureStorage.clear();
    window.location.reload();
  };

  const handleNewChat = () => {
    setActiveChat(null);
    secureStorage.removeItem("activeChat");
    setFeature("Chatbot");
    secureStorage.setItem("feature", "Chatbot");
  };

  const handleChatSelect = (chatId) => {
    setActiveChat(chatId);
    secureStorage.setItem("activeChat", chatId);
    setFeature("Chatbot");
    secureStorage.setItem("feature", "Chatbot");
  };

  const startRenaming = (id, currentTitle) => {
    setEditingChatId(id);
    setEditingTitle(currentTitle);
    setDropdownOpenId(null);
  };

  const saveChatTitle = async (id) => {
    const newTitle = editingTitle.trim();
    if (!newTitle) {
      toast.warn("Title cannot be empty.");
      return;
    }
    try {
      await withAuthRetry(() =>
        axios.put(`${API_BASE_URL}/api/chat/sessions/${id}`, { title: newTitle }, axiosConfig())
      );
      toast.success("Chat renamed");
      setEditingChatId(null);
      fetchRecentChats();
    } catch (error) {
      console.error("Rename failed", error);
      toast.error("Failed to rename chat");
    }
  };

  const handleDeleteChat = async (id) => {
    try {
      await withAuthRetry(() =>
        axios.delete(`${API_BASE_URL}/api/chat/sessions/${id}`, axiosConfig())
      );
      toast.success("Chat deleted");

      if (activeChat === id) {
        setActiveChat(null);
        secureStorage.removeItem("activeChat");
        setFeature("Chatbot");
        secureStorage.setItem("feature", "Chatbot");
      }

      fetchRecentChats();
    } catch (error) {
      console.error("Delete failed", error);
      toast.error("Failed to delete chat");
    }
  };

  const toggleDropdown = (id) => {
    setDropdownOpenId(dropdownOpenId === id ? null : id);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpenId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderProgress = (used, limit, unit = "MB") => {
    const safeUsed = typeof used === "number" ? used : parseFloat(used?.toString().replace(/,/g, "")) || 0;
    const safeLimit = typeof limit === "number" ? limit : parseFloat(limit?.toString().replace(/,/g, "")) || 1;
    const percent = ((safeUsed / safeLimit) * 100).toFixed(1);

    return (
      <div className="usage-box">
        <div className="usage-bar">
          <div className="usage-fill" style={{ width: `${percent}%` }}></div>
        </div>
        <p>
          {`${percent}% of ${safeLimit.toLocaleString()} ${unit} used`}
        </p>
      </div>
    );
  };


  return (
    <>
      {/* Hamburger Button (top-left when sidebar is closed) */}
      {!sidebarOpen && (
        <button
          className={`hamburger-menu ${sidebarOpen ? 'sidebar-open' : ''}`}
          onClick={() => setSidebarOpen(true)}
        >
          ☰
        </button>
      )}
  
      {/* Sidebar Overlay (click to close) */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay show"
          onClick={() => setSidebarOpen(false)}
        />
      )}
  
      {/* Sidebar */}
      <div className={`llm-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <ToastContainer position="bottom-right" />
  
        {/* Close (hamburger) button inside sidebar */}
        <div className="sidebar-header" style={{ position: "relative" }}>
          {sidebarOpen && (
            <button
              className="hamburger-menu sidebar-close-btn"
              onClick={() => setSidebarOpen(false)}
            >
              ✕
            </button>
          )}
        </div>
  
        {/* Scrollable content area */}
        <div className="sidebar-scrollable-content">
          <div>
            <img src={logo} alt="Logo" className="sidebar-logo" />
            <h2 className="sidebar-title">Retreeval Workspace</h2>
            
            <ul className="sidebar-list">
              {isSuperAdmin ? (
                <li className="menu-item" onClick={() => handleFeatureChange("SuperAdminDashboard")}>
                  <span className="sidebar-icon"><MdDashboard /></span> Super Admin Dashboard
                </li>
              ) : (
                <>
                  {/* New Chat */}
                  <li className="menu-item" onClick={() => {
                    handleNewChat();
                    setSidebarOpen(false);
                  }}>
                    <span className="sidebar-icon"><FaComments /></span> New Chat
                  </li>
  
                  {/* Tables & Charts (SQL) */}
                  {currentPlan.toLowerCase() === "free" ? (
                    <div className="disabled-tooltip-wrapper">
                      <li className="menu-item disabled" style={{ pointerEvents: "none", opacity: 0.6 }}>
                        <span className="sidebar-icon"><SiPostgresql /></span> Tables & Charts (SQL)
                      </li>
                      <span className="tooltip-text">Upgrade to use this feature</span>
                    </div>
                  ) : (
                    <li
                      className="menu-item"
                      onClick={() => {
                        handleFeatureChange("SQLQuery");
                        setSidebarOpen(false);
                      }}
                    >
                      <span className="sidebar-icon"><SiPostgresql /></span> Tables & Charts (SQL)
                    </li>
                  )}

                  {/* Tables */}
                  <li
                    className="menu-item"
                    onClick={() => {
                      handleFeatureChange("Tables");
                      setSidebarOpen(false);
                    }}
                  >
                    <span className="sidebar-icon"><MdTableChart /></span> Tables
                  </li>

  
                  {/* Text Summarization */}
                  <li className="menu-item" onClick={() => {
                      handleFeatureChange("TextSummarization");
                      setSidebarOpen(false);
                    }}
                  >
                    <span className="sidebar-icon"><AiOutlineFileText /></span> Text Summarization
                  </li>
  
                  {/* Settings (only for admin) */}
                  {role === "admin" && (
                    <li className="menu-item" onClick={() => {
                        handleFeatureChange("Dashboard");
                        setSidebarOpen(false);
                      }}
                    >
                      <span className="sidebar-icon"><MdDashboard /></span> Settings
                    </li>
                  )}
                </>
              )}
            </ul>
          </div>
  
          {/* Recent Chats Section */}
          {!isSuperAdmin && (
            <>
              {loading ? (
                <p>Loading chats...</p>
              ) : error ? (
                <p className="error-message">{error}</p>
              ) : (
                recentChats.length > 0 && (
                  <div className="recent-chats">
                    <h3 className="recent-chats-title">Recent Chats</h3>
                    {recentChats.map((chat) => (
                      <div key={chat.id} className="recent-chat-item-wrapper">
                        {editingChatId === chat.id ? (
                          <input
                            type="text"
                            className="edit-chat-input"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onBlur={() => saveChatTitle(chat.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                saveChatTitle(chat.id);
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          <li className="recent-chat-item" onClick={() => {
                                handleChatSelect(chat.id);
                                setSidebarOpen(false);
                              }}
                          >
                            {chat.title}
                          </li>
                        )}
                        <div className="chat-actions">
                          <span className="chat-options-button" onClick={() => toggleDropdown(chat.id)}>⋮</span>
                          {dropdownOpenId === chat.id && (
                            <div className="chat-options-dropdown" ref={dropdownRef}>
                              <button onClick={() => startRenaming(chat.id, chat.title)}>Rename</button>
                              <button onClick={() => handleDeleteChat(chat.id)}>Delete</button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </>
          )}
  
          {/* Usage Progress Bars */}
          {!isSuperAdmin && chatUsage && renderProgress(chatUsage.used_mb, chatUsage.limit_mb, "MB")}
          {role === "admin" && orgUsage && renderProgress(orgUsage.used_gb, orgUsage.limit_gb, "GB")}
  
          {!isSuperAdmin &&
            chatTokenUsage?.tokens_used != null &&
            chatTokenUsage?.token_limit && (() => {
              return renderProgress(
                chatTokenUsage.tokens_used,
                chatTokenUsage.token_limit,
                "Tokens"
              );
            })()}
  
          {role === "admin" &&
            orgTokenUsage?.tokens_used != null &&
            orgTokenUsage?.token_cap && (() => {
              return renderProgress(
                orgTokenUsage.tokens_used,
                orgTokenUsage.token_cap,
                "Tokens"
              );
            })()}
        </div>
  
        {/* Fixed Footer Buttons (always visible, not scrollable) */}
        <div className="sidebar-footer">
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
  
          <button className="theme-toggle-button" onClick={toggleTheme}>
            Dark/Light Mode 🌗
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      

      {/* Hamburger Button (top-left when sidebar is closed) */}
      {!sidebarOpen && (
        <button
          className={`hamburger-menu ${sidebarOpen ? 'sidebar-open' : ''}`}
          onClick={() => setSidebarOpen(true)}
        >
          ☰
        </button>
      )}

      {/* Sidebar Overlay (click to close) */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay show"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`llm-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <ToastContainer position="bottom-right" />

        {/* Close (hamburger) button inside sidebar */}

        <div className="sidebar-header" style={{ position: "relative" }}>


          {sidebarOpen && (
            <button
              className="hamburger-menu sidebar-close-btn"
              onClick={() => setSidebarOpen(false)}
            >
              ✕
            </button>
          )}
          {/* <button class="sidebar-close-btn">×</button> */}
        </div>

    
  
        <div>
          <img src={logo} alt="Logo" className="sidebar-logo" />
          <h2 className="sidebar-title">Retreeval Workspace</h2>
          <ul className="sidebar-list">
            {isSuperAdmin ? (
              <li className="menu-item" onClick={() => handleFeatureChange("SuperAdminDashboard")}>
                <span className="sidebar-icon"><MdDashboard /></span> Super Admin Dashboard
              </li>
            ) : (
              <>

                <li className="menu-item" onClick={() => {
                  handleNewChat();
                  setSidebarOpen(false);
                }}>
                  <span className="sidebar-icon"><FaComments /></span> New Chat
                </li>
  
                {loading ? (
                  <p>Loading chats...</p>
                ) : error ? (
                  <p className="error-message">{error}</p>
                ) : (
                  recentChats.length > 0 && (
                    <div className="recent-chats">
                      <h3 className="recent-chats-title">Recent Chats</h3>
                      {recentChats.map((chat) => (
                        <div key={chat.id} className="recent-chat-item-wrapper">
                          {editingChatId === chat.id ? (
                            <input
                              type="text"
                              className="edit-chat-input"
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onBlur={() => saveChatTitle(chat.id)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  saveChatTitle(chat.id);
                                }
                              }}
                              autoFocus
                            />
                          ) : (
                            <li className="recent-chat-item" onClick={() => {
                                  handleChatSelect(chat.id);
                                  setSidebarOpen(false);
                                }}
                            >
                              {chat.title}
                            </li>
                          )}
                          <div className="chat-actions">
                            <span className="chat-options-button" onClick={() => toggleDropdown(chat.id)}>⋮</span>
                            {dropdownOpenId === chat.id && (
                              <div className="chat-options-dropdown" ref={dropdownRef}>
                                <button onClick={() => startRenaming(chat.id, chat.title)}>Rename</button>
                                <button onClick={() => handleDeleteChat(chat.id)}>Delete</button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}

                {currentPlan.toLowerCase() === "free" ? (
                  <div className="disabled-tooltip-wrapper">
                    <li className="menu-item disabled" style={{ pointerEvents: "none", opacity: 0.6 }}>
                      <span className="sidebar-icon"><SiPostgresql /></span> Tables & Charts (SQL)
                    </li>
                    <span className="tooltip-text">Upgrade to use this feature</span>
                  </div>
                ) : (
                  <li
                    className="menu-item"
                    onClick={() => {
                      handleFeatureChange("SQLQuery");
                      setSidebarOpen(false);
                    }}
                  >
                    <span className="sidebar-icon"><SiPostgresql /></span> Tables & Charts (SQL)
                  </li>
                )}

                <li
                  className="menu-item"
                  onClick={() => {
                    handleFeatureChange("Tables");
                    setSidebarOpen(false);
                  }}
                >
                  <span className="sidebar-icon"><MdTableChart /></span> Tables
                </li>


                <li className="menu-item" onClick={() => {
                    handleFeatureChange("TextSummarization");
                    setSidebarOpen(false);
                  }}
                >
                  <span className="sidebar-icon"><AiOutlineFileText /></span> Text Summarization
                </li>
                {role === "admin" && (
                  <li className="menu-item" onClick={() => {
                      handleFeatureChange("Dashboard");
                      setSidebarOpen(false);
                    }}
                  >
                    <span className="sidebar-icon"><MdDashboard /></span> Settings
                  </li>
                )}
              </>
            )}
          </ul>
        </div>
  
        {/* Usage Sections */}
        {!isSuperAdmin && chatUsage && renderProgress(chatUsage.used_mb, chatUsage.limit_mb, "MB")}
        {role === "admin" && orgUsage && renderProgress(orgUsage.used_gb, orgUsage.limit_gb, "GB")}
  
        {!isSuperAdmin &&
          chatTokenUsage?.tokens_used != null &&
          chatTokenUsage?.token_limit && (() => {
            return renderProgress(
              chatTokenUsage.tokens_used,
              chatTokenUsage.token_limit,
              "Tokens"
            );
          })()}
  
        {role === "admin" &&
          orgTokenUsage?.tokens_used != null &&
          orgTokenUsage?.token_cap && (() => {
            return renderProgress(
              orgTokenUsage.tokens_used,
              orgTokenUsage.token_cap,
              "Tokens"
            );
          })()}
  
        {/* Footer Buttons */}
        <button className="logout-button" onClick={handleLogout}>
          Logout
        </button>
  
        <button className="theme-toggle-button" onClick={toggleTheme}>
          Dark/Light Mode 🌗
        </button>
      </div>
    </>
  );

};

export default LLMSidebar;
